import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  BookOpen,
  CalendarRange,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CookingPot,
  Hourglass,
  Info,
  Plus,
  Repeat,
  Target,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { PastillaDeEtapa } from '@/components/PastillaDeEtapa';
import { Casilla } from '@/components/Casilla';
import { Banner } from '@/components/Banner';
import { Fila, Filas } from '@/components/Fila';
import { ICONO_TIEMPO } from '@/components/ListaComidas';
import { leerDiaDeComida, marcarRegistroIncompleto } from '@/db/comidas';
import { habitualesDe, plantillaHabitualDe } from '@/db/habituales';
import { leerMetasDelDia } from '@/db/metas';
import { leerSettings } from '@/db/schema';
import { mensajeDeError } from '@/db/tx';
import type { ComidaHabitual, MealEntry } from '@/db/types';
import { formatearCorto, nombreDia, sumarDias } from '@/domain/dates';
import { pendientesDelDia } from '@/domain/habituales';
import {
  agruparPorTiempo,
  calidadParaAvisar,
  estadoDe,
  formatearGramos,
  formatearKcal,
  macrosIncompletos,
  registrosIncompletos,
  sumar,
  textoCantidad,
  type GrupoTiempo,
} from '@/domain/nutricion';
import { useFechaHoy } from '@/lib/useFechaHoy';
import { Etiqueta, EtiquetaCalidad, EtiquetaEstado, esTiempoComida, fechaDeParametro } from './comunes';
import { AvisoHabitual, TarjetaHabituales } from './HabitualesPages';
import { ResumenDelDia } from './ResumenDelDia';

/**
 * Comida: el día por tiempos de comida, con subtotales, totales contra la
 * meta y lo que falta (Req. 18.5, 18.8 a 18.10). Se mueve entre días con
 * `?fecha=AAAA-MM-DD`; sin fecha es hoy.
 */
export function FoodPage() {
  const hoy = useFechaHoy();
  const [parametros, setParametros] = useSearchParams();
  const fecha = fechaDeParametro(parametros.get('fecha'), hoy);
  const tiempoEnfocado = parametros.get('tiempo');
  const dia = useLiveQuery(() => leerDiaDeComida(fecha), [fecha]);
  const metas = useLiveQuery(() => leerMetasDelDia(fecha), [fecha]);
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const [error, setError] = useState<string | null>(null);
  const [porQueIncompleto, setPorQueIncompleto] = useState(false);

  // Desde Hoy, la palomita de una comida trae aquí con ese tiempo a la vista.
  // Solo una vez por día y tiempo: guardar algo después no vuelve a mover la
  // pantalla (`dia` cambia con cada escritura).
  const desplazado = useRef<string | null>(null);
  const cargado = dia !== undefined;
  useEffect(() => {
    if (!cargado || !esTiempoComida(tiempoEnfocado)) return;
    const clave = `${fecha}|${tiempoEnfocado}`;
    if (desplazado.current === clave) return;
    desplazado.current = clave;
    document.getElementById(`tiempo-${tiempoEnfocado}`)?.scrollIntoView({ block: 'start' });
  }, [cargado, fecha, tiempoEnfocado]);

  if (dia === undefined || metas === undefined || ajustes === undefined) return null;

  const esHoy = fecha === hoy;
  const grupos = agruparPorTiempo(dia.entradas);
  const totales = sumar(dia.entradas);
  // Comidas habituales (0.3.5): se proponen en su día; nada se registra solo
  const persona = ajustes.rutinasDe ?? null;
  const habituales = habitualesDe(ajustes);
  const pendientes = pendientesDelDia(habituales, fecha, hoy, dia.entradas);
  const conHabituales = persona === null || habituales.length > 0 || plantillaHabitualDe(persona) !== null;

  function irA(nueva: string) {
    setParametros(nueva === hoy ? {} : { fecha: nueva }, { replace: true });
  }

  async function cambiarIncompleto(marcado: boolean) {
    setError(null);
    try {
      await marcarRegistroIncompleto(fecha, marcado);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  return (
    <div>
      <PageHeader compacto titulo="Tu comida" />

      {/* El día, al centro y con las flechas a los lados (reacomodo 0.11.0) */}
      <nav aria-label="Cambiar de día" className="-mt-2 mb-2 flex items-center justify-center gap-1">
        <button
          type="button"
          aria-label="Día anterior"
          onClick={() => irA(sumarDias(fecha, -1))}
          className="grid size-11 place-items-center rounded-xl text-marca active:opacity-60"
        >
          <ChevronLeft className="size-5" />
        </button>
        <p className="min-w-[132px] text-center text-[15px] font-medium text-texto">
          {esHoy ? `Hoy, ${formatearCorto(fecha)}` : conMayuscula(`${nombreDia(fecha)}, ${formatearCorto(fecha)}`)}
        </p>
        <button
          type="button"
          aria-label="Día siguiente"
          onClick={() => irA(sumarDias(fecha, 1))}
          className="grid size-11 place-items-center rounded-xl text-marca active:opacity-60"
        >
          <ChevronRight className="size-5" />
        </button>
      </nav>

      <div className="mb-4 flex flex-wrap items-center justify-center gap-2">
        {!esHoy && (
          <button
            type="button"
            onClick={() => irA(hoy)}
            className="inline-flex min-h-11 items-center rounded-xl bg-marca-suave px-3 text-[13px] font-medium text-texto"
          >
            Ir a hoy
          </button>
        )}
        {metas.posicion !== null && <PastillaDeEtapa posicion={metas.posicion} />}
      </div>

      <ResumenDelDia totales={totales} metas={metas.metas} incompletos={registrosIncompletos(dia.entradas)} />

      {esHoy && (
        <div className="mt-4 empty:hidden">
          <TarjetaHabituales persona={persona} existentes={habituales} />
        </div>
      )}

      <section aria-labelledby="titulo-tus-comidas" className="mt-5">
        <h2 id="titulo-tus-comidas" className="mb-2 text-[17px] tracking-[-0.25px] text-texto">
          Tus comidas
        </h2>
        <div className="space-y-2">
          {grupos.map((grupo) => (
            <TiempoDeComida
              key={grupo.tiempo}
              grupo={grupo}
              fecha={fecha}
              pendientes={pendientes.filter((h) => h.tiempo === grupo.tiempo)}
              abiertoAlInicio={tiempoEnfocado === grupo.tiempo}
            />
          ))}
        </div>
      </section>

      <section className="mt-5" aria-labelledby="titulo-registro-incompleto">
        <h2 id="titulo-registro-incompleto" className="sr-only">
          Registro del día
        </h2>
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <Casilla marcada={dia.registroIncompleto} onCambio={(m) => void cambiarIncompleto(m)}>
              No registré todo lo que comí
            </Casilla>
          </div>
          <button
            type="button"
            aria-expanded={porQueIncompleto}
            aria-label="Para qué sirve marcar el día como incompleto"
            onClick={() => setPorQueIncompleto((v) => !v)}
            className="mt-1 grid size-11 shrink-0 place-items-center rounded-full text-texto-suave active:opacity-60"
          >
            <Info className="size-[18px]" />
          </button>
        </div>
        {porQueIncompleto && (
          <p className="mt-1 text-[12px] leading-relaxed text-texto-suave">
            Un día marcado así no se usa para estimar tu mantenimiento ni para calcular tu ayuno.
          </p>
        )}
        {error !== null && (
          <div className="mt-2">
            <Banner tono="alerta" titulo="No se pudo guardar">
              {error}
            </Banner>
          </div>
        )}
      </section>

      <Filas tarjetas rejilla className="mt-5">
        {conHabituales && <Fila tarjeta icono={Repeat} titulo="Habituales" a="/comida/habituales" />}
        <Fila tarjeta icono={BookOpen} titulo="Mis alimentos" a="/comida/mis-alimentos" />
        <Fila tarjeta icono={CookingPot} titulo="Mis platillos" a="/comida/platillos" />
        <Fila tarjeta icono={Hourglass} titulo="Mis ayunos" a="/comida/ayunos" />
        <Fila tarjeta icono={CalendarRange} titulo="Mi plan" a="/mas/plan" />
        <Fila tarjeta icono={Target} titulo="Metas sin plan" a="/mas/metas" />
      </Filas>
    </div>
  );
}

const conMayuscula = (t: string) => t.charAt(0).toLocaleUpperCase('es-MX') + t.slice(1);

/**
 * Un tiempo de comida en su tarjeta (reacomodo 0.11.0): el emblema, lo que
 * lleva ese tiempo (cuántos alimentos, la hora del primero, los macros y las
 * kcal) y, desplegable, la lista de lo registrado.
 *
 * Empieza cerrado para que el día quepa de un vistazo, y abierto cuando se
 * llega desde Hoy con `?tiempo=`. Lo que se puede hacer no se esconde: una
 * comida habitual pendiente y el botón de agregar siempre se ven.
 */
function TiempoDeComida({
  grupo,
  fecha,
  pendientes,
  abiertoAlInicio,
}: {
  grupo: GrupoTiempo;
  fecha: string;
  /** Las comidas habituales de este tiempo que tocan ese día y no se han registrado. */
  pendientes: readonly ComidaHabitual[];
  abiertoAlInicio: boolean;
}) {
  const { tiempo, etiqueta, entradas, totales } = grupo;
  const [abierto, setAbierto] = useState(abiertoAlInicio);
  const agregar = `/comida/agregar?fecha=${fecha}&tiempo=${tiempo}`;
  const Icono = ICONO_TIEMPO[tiempo];
  const hay = entradas.length > 0;
  const incompletos = totales.sinDato.proteinaG + totales.sinDato.carbohidratosG + totales.sinDato.grasaG > 0;
  const cuantos = entradas.length === 1 ? '1 alimento' : `${entradas.length} alimentos`;
  const hora = entradas[0]?.hora;

  return (
    <section
      id={`tiempo-${tiempo}`}
      aria-labelledby={`titulo-${tiempo}`}
      className="scroll-mt-4 rounded-panel bg-superficie p-4"
    >
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-superficie-suave text-marca">
          <Icono aria-hidden="true" className="size-[18px]" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 id={`titulo-${tiempo}`} className="text-[17px] leading-snug tracking-[-0.25px] text-texto">
            {etiqueta}
          </h2>
          <p className="mt-0.5 text-[12px] text-texto-suave">
            {hay ? [cuantos, hora].filter(Boolean).join(' · ') : 'Sin registros'}
          </p>
          {hay && (
            <p className="text-[12px] tabular-nums text-texto-suave">
              {`P ${formatearGramos(totales.proteinaG)} · C ${formatearGramos(totales.carbohidratosG)} · G ${formatearGramos(totales.grasaG)}`}
              {incompletos ? ' · datos incompletos' : ''}
            </p>
          )}
        </div>
        {hay && (
          <div className="flex shrink-0 items-center gap-1">
            <span className="whitespace-nowrap text-[13px] tabular-nums text-texto">{formatearKcal(totales.kcal)}</span>
            <button
              type="button"
              aria-expanded={abierto}
              aria-controls={`lista-${tiempo}`}
              aria-label={`${abierto ? 'Ocultar' : 'Ver'} lo registrado en ${etiqueta.toLowerCase()}`}
              onClick={() => setAbierto((v) => !v)}
              className="grid size-10 place-items-center rounded-full text-texto-suave active:opacity-60"
            >
              <ChevronDown className={`size-[18px] transition-transform ${abierto ? 'rotate-180' : ''}`} />
            </button>
          </div>
        )}
      </div>

      {hay && abierto && (
        <ul id={`lista-${tiempo}`} className="mt-2 divide-y divide-borde border-t border-borde">
          {entradas.map((e) => (
            <FilaRegistro key={e.id} entrada={e} />
          ))}
        </ul>
      )}

      {pendientes.map((h) => (
        <AvisoHabitual key={h.id} habitual={h} fecha={fecha} />
      ))}

      <Link
        to={agregar}
        className="mt-1 inline-flex min-h-11 items-center gap-2 rounded-xl text-[13px] font-medium text-marca active:opacity-60"
      >
        <span className="grid size-7 place-items-center rounded-full bg-marca-suave">
          <Plus className="size-4" />
        </span>
        Agregar a {etiqueta.toLowerCase()}
      </Link>
    </section>
  );
}

function FilaRegistro({ entrada }: { entrada: MealEntry }) {
  const cantidad = textoCantidad(entrada);
  const incompleto = macrosIncompletos(entrada.valores);
  return (
    <li>
      <Link
        to={`/comida/registro/${entrada.id}`}
        className="flex min-h-14 items-center gap-3 py-3 active:opacity-60"
      >
        <span className="grid min-w-0 flex-1 gap-1">
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="text-[14px] font-medium text-texto">{entrada.nombre}</span>
            <EtiquetaEstado estado={estadoDe(entrada)} />
            <EtiquetaCalidad calidad={calidadParaAvisar(entrada)} />
            {incompleto && <Etiqueta tono="aviso">Sin macros completos</Etiqueta>}
          </span>
          <span className="text-[12px] text-texto-suave">
            {[cantidad ?? 'Registro rápido', entrada.hora].join(' · ')}
          </span>
        </span>
        <span className="shrink-0 text-[13px] tabular-nums text-texto">{formatearKcal(entrada.valores.kcal)}</span>
        <ChevronRight className="size-4 text-texto-suave" />
      </Link>
    </li>
  );
}
